import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { QuestionRendererProps } from './types';
export function ThermometerSliderRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  const config = question.data || {};
  const minTemp = typeof config.minTemp === 'number' ? config.minTemp : -20;
  const maxTemp = typeof config.maxTemp === 'number' ? config.maxTemp : 10;
  const startTemp = typeof config.startTemp === 'number' ? config.startTemp : 0;
  const step = typeof config.step === 'number' ? config.step : 1;
  const unit = config.unit || 'C';
  const [value, setValue] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);
  const prevValidateRef = useRef(false);
  useEffect(() => {
    setValue(null);
    prevValidateRef.current = false;
  }, [question.id]);
  useEffect(() => {
    if (!shouldValidate) {
      prevValidateRef.current = false;
    }
  }, [shouldValidate]);
  useEffect(() => {
    onReadyChange?.(value !== null);
  }, [value, onReadyChange]);
  useEffect(() => {
    if (shouldValidate && !prevValidateRef.current) {
      prevValidateRef.current = true;
      onAnswer(value ?? startTemp);
    }
  }, [shouldValidate, value, onAnswer, startTemp]);
  const currentTemp = value !== null ? value : startTemp;
  const computed = useMemo(() => {
    const range = maxTemp - minTemp;
    const currentPercent = (currentTemp - minTemp) / range * 100;
    const startPercent = (startTemp - minTemp) / range * 100;
    const tickInterval = range <= 40 ? 5 : 10;
    const ticks: number[] = [];
    for (let temp = Math.ceil(minTemp / tickInterval) * tickInterval; temp <= maxTemp; temp += tickInterval) {
      ticks.push(temp);
    }
    return {
      range,
      currentPercent: Math.max(0, Math.min(100, currentPercent)),
      startPercent: Math.max(0, Math.min(100, startPercent)),
      ticks,
      tickInterval
    };
  }, [minTemp, maxTemp, currentTemp, startTemp]);
  const tubeTop = 20;
  const tubeHeight = 200;
  const tubeBottom = tubeTop + tubeHeight;
  const bulbCy = tubeBottom + 16;
  const svgHeight = bulbCy + 24;
  const svgWidth = 140;
  const handlePointerEvent = useCallback((e: React.PointerEvent | PointerEvent) => {
    if (!svgRef.current) return;
    // Prevent default to stop scrolling while dragging
    if (e.type !== 'pointerdown') {
      e.preventDefault();
    }
    const rect = svgRef.current.getBoundingClientRect();
    const rawY = e.clientY - rect.top;
    // Account for SVG viewBox scaling
    const scale = rect.height / svgHeight;
    const y = rawY / scale;
    // The active dragging area is between y=25 and y=215
    const activeTop = tubeTop + 5;
    const activeBottom = tubeBottom - 5;
    const activeHeight = activeBottom - activeTop;
    // Clamp y to the active area
    const clampedY = Math.max(activeTop, Math.min(activeBottom, y));
    // Calculate percentage (0 at bottom, 1 at top)
    const percent = 1 - (clampedY - activeTop) / activeHeight;
    // Calculate raw temperature
    const rawTemp = minTemp + percent * (maxTemp - minTemp);
    // Snap to step
    const snappedTemp = Math.round(rawTemp / step) * step;
    // Clamp to min/max just in case
    const finalTemp = Math.max(minTemp, Math.min(maxTemp, snappedTemp));
    setValue(finalTemp);
  }, [minTemp, maxTemp, step, tubeTop, tubeBottom, svgHeight]);
  const onPointerDown = (e: React.PointerEvent) => {
    // Only left click or touch
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    // Capture pointer so we can track outside the SVG
    const target = e.target as Element;
    if (target.setPointerCapture) {
      target.setPointerCapture(e.pointerId);
    }
    setIsDragging(true);
    handlePointerEvent(e);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!isDragging) return;
    handlePointerEvent(e);
  };
  const onPointerUp = (e: React.PointerEvent) => {
    setIsDragging(false);
    const target = e.target as Element;
    if (target.releasePointerCapture) {
      target.releasePointerCapture(e.pointerId);
    }
  };
  return <div className="w-full max-w-md mx-auto p-6 sm:p-8 bg-white rounded-3xl border-2 border-slate-200 shadow-xl" data-id="element-8806">
      <div className="text-center mb-8" data-id="element-8807">
        <h3 className="text-xl font-bold text-slate-700 mb-3" data-id="element-8808">
          Sleep de thermometer
        </h3>
        <div className="inline-flex items-center justify-center min-w-[120px] h-16 px-6 bg-rose-50 rounded-2xl border-2 border-rose-100" data-id="element-8809">
          <span className="text-3xl font-black text-rose-600" data-id="element-8810">
            {value !== null ? value : '?'}
          </span>
          <span className="text-lg font-bold text-rose-400 ml-2" data-id="element-8811">°{unit}</span>
        </div>
      </div>

      <div className="relative flex justify-center items-center select-none" data-id="element-8812">
        <svg ref={svgRef} width={svgWidth} height={svgHeight} viewBox={`0 0 ${svgWidth} ${svgHeight}`} className="drop-shadow-sm overflow-visible touch-none cursor-pointer" onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp} data-id="element-8813">
          {/* Thermometer tube */}
          <rect x="60" y={tubeTop} width="20" height={tubeHeight} rx="10" fill="white" stroke="#cbd5e1" strokeWidth="2" data-id="element-8814" />

          {/* Scale markings */}
          {computed.ticks.map(temp => {
          const percent = (temp - minTemp) / computed.range * 100;
          const y = tubeBottom - percent / 100 * (tubeHeight - 10) - 5;
          const isMajor = temp % (computed.tickInterval * 2) === 0 || temp === 0;
          return <g key={temp} data-id="element-8815">
                <line x1={isMajor ? '45' : '52'} y1={y} x2="60" y2={y} stroke="#94a3b8" strokeWidth={isMajor ? '2' : '1'} data-id="element-8816" />
                {isMajor && <text x="40" y={y + 4} textAnchor="end" fontSize="12" fill="#64748b" fontWeight="700" data-id="element-8817">
                    {temp}°
                  </text>}
              </g>;
        })}

          {/* Start Temperature Marker */}
          <g data-id="element-8818">
            <line x1="80" y1={tubeBottom - computed.startPercent / 100 * (tubeHeight - 10) - 5} x2="95" y2={tubeBottom - computed.startPercent / 100 * (tubeHeight - 10) - 5} stroke="#94a3b8" strokeWidth="2" strokeDasharray="4 2" data-id="element-8819" />
            <text x="100" y={tubeBottom - computed.startPercent / 100 * (tubeHeight - 10) - 1} fontSize="11" fill="#64748b" fontWeight="600" data-id="element-8820">
              Start: {startTemp}°
            </text>
          </g>

          {/* Mercury column (animated) */}
          <motion.rect x="64" width="12" rx="6" fill="#ef4444" initial={false} animate={{
          height: computed.currentPercent / 100 * (tubeHeight - 10) + 15,
          y: tubeBottom - computed.currentPercent / 100 * (tubeHeight - 10) - 5
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 25
        }} data-id="element-8821" />

          {/* Bulb at bottom */}
          <circle cx="70" cy={bulbCy} r="16" fill="#dc2626" stroke="#b91c1c" strokeWidth="2" data-id="element-8822" />
          <circle cx="70" cy={bulbCy} r="10" fill="#ef4444" data-id="element-8823" />

          {/* Drag Handle Indicator */}
          <motion.g initial={false} animate={{
          y: tubeBottom - computed.currentPercent / 100 * (tubeHeight - 10) - 5
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 25
        }} data-id="element-8824">
            <circle cx="70" cy="0" r="14" fill="white" stroke="#ef4444" strokeWidth="3" data-id="element-8825" />
            <path d="M66 -3 L74 -3 M66 0 L74 0 M66 3 L74 3" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" data-id="element-8826" />
          </motion.g>
        </svg>

        {/* Hidden input for accessibility */}
        <input type="range" min={minTemp} max={maxTemp} step={step} value={currentTemp} onChange={e => setValue(Number(e.target.value))} className="sr-only" aria-label="Temperatuur slider" data-id="element-8827" />
      </div>

      <p className="text-center text-slate-500 text-sm font-medium mt-6" data-id="element-8828">
        Sleep de rode knop omhoog of omlaag
      </p>
    </div>;
}